import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { useTranslation } from 'react-i18next'
import { fetchManagedForm, publishForm, saveDraft, unpublishForm } from './api'
import { configuration, ManagedForm } from './public-form'

import { FormMutation } from './draft-state'
const formKey = (campaignId: string) => ['campaign-public-form', campaignId]

export function useManagedForm(campaignId: string, enabled = true) {
    return useQuery({
        queryKey: formKey(campaignId),
        enabled,
        queryFn: () => fetchManagedForm(campaignId),
        refetchOnWindowFocus: false,
    })
}

export function useUpdatePublicForm(campaignId: string, onSaved: (result: ManagedForm, input: FormMutation) => void) {
    const client = useQueryClient()
    const { t } = useTranslation()
    const [notice, contextHolder] = notification.useNotification()
    const mutation = useMutation({
        mutationFn: async (input: FormMutation) => {
            if (input.action === 'unpublish') return unpublishForm(campaignId)
            if (!configuration.safeParse(input.draft).success)
                throw new Error(t('Complete the brand name, heading, and button label.'))
            const result = await saveDraft(campaignId, input.draft)
            client.setQueryData(formKey(campaignId), result)
            return input.action === 'publish' ? publishForm(campaignId) : result
        },
        onSuccess: (result, input) => {
            client.setQueryData(formKey(campaignId), result)
            onSaved(result, input)
            notice.success({
                message: t(
                    input.action === 'publish'
                        ? 'Form published'
                        : input.action === 'unpublish'
                          ? 'Form unpublished'
                          : 'Draft saved'
                ),
                description: input.action === 'publish' ? t('Your changes are live on the public form.') : undefined,
            })
        },
    })
    return { mutation, contextHolder }
}
